<template>
  <div class="card mb-4">
    <div class="card-header"><h5 class="mb-0">Calculated Results</h5></div>
    <div class="card-body">

      <p v-if="!hasResults && !saving" class="text-muted small mb-3">
        Fill in the project details to see calculated results.
      </p>

      <template v-if="hasResults || saving">

      <h6 class="text-uppercase text-muted small fw-bold mt-2 mb-2">Sprint Breakdown</h6>
      <table class="table table-sm">
        <tbody>
          <tr>
            <td>
              Base Effort
              <i class="fal fa-info-circle calc-info" title="Effective productive hours per day after applying the efficiency factor. Calc: Working Hours/Day × Efficiency"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.base_effort.toFixed(2) }} hrs/day</template>
            </td>
          </tr>
          <tr>
            <td>
              Base Sprints
              <i class="fal fa-info-circle calc-info" title="Sprints required to deliver the base scope at the current team size. Calc: Base Effort Estimation / Base Effort / Dev Days, rounded up to nearest 0.5"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.base_sprints.toFixed(2) }} sprint(s)</template>
            </td>
          </tr>
          <tr>
            <td>
              Sprint Rituals
              <i class="fal fa-info-circle calc-info" title="Total hours spent in rituals (standups, planning, retros) across all sprints and devs. Calc: Sprint Rituals Estimate × Base Sprints × No. of Developers"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.sprint_rituals.toFixed(2) }} hrs</template>
            </td>
          </tr>
          <tr>
            <td>
              Effort
              <i class="fal fa-info-circle calc-info" title="Total effective working days once ritual overhead is factored in. Calc: (Base Effort Estimation + Sprint Rituals) / Base Effort"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.effort.toFixed(2) }} days</template>
            </td>
          </tr>
          <tr>
            <td>
              Sprints
              <i class="fal fa-info-circle calc-info" title="Raw sprint count including contingency buffer, before rounding. Calc: (Effort + Contingency Days) / Dev Days / No. of Developers"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.sprints.toFixed(4) }}</template>
            </td>
          </tr>
          <tr class="table-active fw-bold">
            <td>
              Sprint to Book
              <i class="fal fa-info-circle calc-info" title="The number of sprints to invoice the client for. Calc: Sprints rounded up to the nearest 0.5"></i>
            </td>
            <td class="text-end">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.sprint_to_book.toFixed(2) }} sprint(s)</template>
            </td>
          </tr>
        </tbody>
      </table>

      <h6 class="text-uppercase text-muted small fw-bold mt-3 mb-2">Development</h6>
      <table class="table table-sm">
        <tbody>
          <tr>
            <td>
              Billable Dev Hours
              <i class="fal fa-info-circle calc-info" title="Developer coding days converted to billable hours. Calc: Effort × Working Hours/Day"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.billable_dev_hours.toFixed(2) }} hrs</template>
            </td>
          </tr>
          <tr>
            <td>
              Dev - Bug Fixing
              <i class="fal fa-info-circle calc-info" title="Days per dev allocated to bug fixing; the remaining sprint days not spent on feature development. Calc: (10 - Dev Days) × Sprint to Book × No. of Developers"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.dev_bug_fixing.toFixed(2) }} days</template>
            </td>
          </tr>
          <tr>
            <td>
              Warranty
              <i class="fal fa-info-circle calc-info" title="Post-delivery support hours included in the project cost. Calc: Base Effort Estimation × Warranty Percentage"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.warranty.toFixed(2) }} hrs</template>
            </td>
          </tr>
        </tbody>
      </table>

      <h6 class="text-uppercase text-muted small fw-bold mt-3 mb-2">Scoping &amp; Testing</h6>
      <table class="table table-sm">
        <tbody>
          <tr>
            <td>
              Scoping Sprint Testing
              <i class="fal fa-info-circle calc-info" title="Total QA/testing days across all booked sprints. Calc: Sprint to Book × Scoping Sprint Testing"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.scoping_sprint_testing_total.toFixed(2) }} days</template>
            </td>
          </tr>
          <tr>
            <td>
              Project Management
              <i class="fal fa-info-circle calc-info" title="Total PM hours across all booked sprints. Calc: Project Management (hrs/sprint) × Sprint to Book"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.project_management_total.toFixed(2) }} hrs</template>
            </td>
          </tr>
          <tr>
            <td>
              Evaluation
              <i class="fal fa-info-circle calc-info" title="Days allocated for scoping, discovery, or solution evaluation. Direct input."></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.evaluation }} days</template>
            </td>
          </tr>
          <tr>
            <td>
              Contingency
              <i class="fal fa-info-circle calc-info" title="To cater for leave/sick days and unforseen complications"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.contingency_days }} days</template>
            </td>
          </tr>
        </tbody>
      </table>

      <h6 class="text-uppercase text-muted small fw-bold mt-3 mb-2">Totals</h6>
      <table class="table table-sm">
        <tbody>
          <tr>
            <td>
              Total Billable Hours
              <i class="fal fa-info-circle calc-info" title="Sum of all billable time across the project. Calc: Dev Hours + Warranty + Rituals + PM Hours + (Testing×Devs + Bug Fixing×Devs + Evaluation + Contingency) × Working Hours/Day"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>{{ projectCalculation.total_billable_hours.toFixed(2) }} hrs</template>
            </td>
          </tr>
          <tr>
            <td>
              Dev Budget
              <i class="fal fa-info-circle calc-info" title="Total cost of development work before licence. Calc: Total Billable Hours × Hourly Rate"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>${{ projectCalculation.dev_budget | currency }}</template>
            </td>
          </tr>
          <tr>
            <td>
              License
              <i class="fal fa-info-circle calc-info" title="Platform licence fee applied on top of the development cost. Calc: Dev Budget × 25%"></i>
            </td>
            <td class="text-end text-muted">
              <span v-if="saving" class="skeleton"></span>
              <template v-else>${{ projectCalculation.license | currency }}</template>
            </td>
          </tr>
          <tr class="table-success fw-bold fs-5">
            <td>
              Total Budget
              <i class="fal fa-info-circle calc-info" title="Full project cost including licence. Calc: Dev Budget + License"></i>
            </td>
            <td class="text-end">
              <span v-if="saving" class="skeleton skeleton-lg"></span>
              <template v-else>${{ projectCalculation.total_budget | currency }}</template>
            </td>
          </tr>
        </tbody>
      </table>

      </template>

    </div>
  </div>
</template>

<script>
export default {
  props: {
    projectCalculation: { type: Object, required: true },
    saving: { type: Boolean, default: false },
  },

  computed: {
    hasResults() {
      return !!this.projectCalculation.id && this.projectCalculation.sprint_to_book != null
    },
  },
}
</script>

<style scoped>
.calc-info {
  color: #6c757d;
  font-size: 0.75rem;
  margin-left: 3px;
  vertical-align: middle;
  opacity: 0.6;
}
.calc-info:hover {
  opacity: 1;
}
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.skeleton {
  display: inline-block;
  width: 80px;
  height: 0.85em;
  background: linear-gradient(90deg, #e9ecef 25%, #dee2e6 50%, #e9ecef 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: 3px;
  vertical-align: middle;
}
.skeleton-lg {
  width: 110px;
  height: 1em;
}
</style>
